import { CopyIcon } from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { IconButton } from "@/components/IconButton/IconButton";
import type { HotelData } from "@/features/hotels/types/types";
import { copyToClipboardWithFeedback } from "@/utils/copyToClipboard";

type Props = {
  hotel: HotelData;
};

export const HotelContactDetails = ({ hotel }: Props) => {
  const intl = useIntl();

  if (!hotel.address && !hotel.email && !hotel.telephone) {
    return null;
  }

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        defaultMessage: "Hotel information",
        description: "Section heading for the hotel's contact information",
      })}
      expandedByDefault={false}
    >
      <div className="flex flex-col justify-between gap-4 text-sm text-text-primary">
        {hotel.address && (
          <div className="flex justify-between gap-4">
            {intl.formatMessage({
              defaultMessage: "Address",
              description:
                "Label for the hotel's physical address on the hotel details page",
            })}
            <div className="flex w-210 flex-wrap justify-end text-end">
              {hotel.address}
            </div>
          </div>
        )}
        {hotel.email && (
          <div className="flex items-center justify-between gap-4">
            {intl.formatMessage({
              defaultMessage: "Email",
              description: "Label for the hotel's email contact address",
            })}
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
              <span className="text-end break-all">{hotel.email}</span>
              <IconButton
                color="interactive"
                onClick={() =>
                  copyToClipboardWithFeedback(
                    hotel.email,
                    intl.formatMessage({
                      defaultMessage: "Hotel email copied",
                      description:
                        "Snackbar shown after copying the hotel email address",
                    }),
                  )
                }
                aria-label={intl.formatMessage({
                  defaultMessage: "Copy email",
                  description:
                    "Button to copy the hotel email address to clipboard",
                })}
              >
                <CopyIcon size={20} />
              </IconButton>
            </div>
          </div>
        )}
        {hotel.telephone && (
          <div className="flex justify-between gap-4">
            {intl.formatMessage({
              defaultMessage: "Phone",
              description: "Label for the hotel's phone number",
            })}
            <a
              className="text-text-interactive underline"
              href={`tel:${hotel.telephone}`}
              dir="ltr"
            >
              {hotel.telephone}
            </a>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};
